"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js/max";
import { Icon } from "./Icon";
import { cn } from "@/lib/utils";
import { validatePhoneNumber } from "@/lib/phone-validation";
import { PHONE_MAX_LENGTH } from "@/lib/constants";
import { COUNTRIES as COUNTRY_LIST } from "@/lib/countries";

/**
 * Country + national-number pair that outputs a single E.164 string — the
 * format the real API requires for every contact-phone field (Host, Supplier).
 * Dial-code picker + number in one bordered field, matching the b2c app's
 * PhoneNumberInput (dial code | divider | number, searchable country list).
 */

const DEFAULT_COUNTRY: CountryCode = "AE";

interface CountryOption {
  code: CountryCode;
  name: string;
  dialCode: string;
}

const COUNTRIES: CountryOption[] = COUNTRY_LIST.map((c) => ({
  code: c.code as CountryCode,
  name: c.name,
  dialCode: `+${getCountryCallingCode(c.code as CountryCode)}`,
}));

export interface PhoneNumberFieldProps {
  /** The full E.164 value (e.g. "+390559901234"), or "" when empty — same contract as a plain text input. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Suppress the internal error line — use when the parent renders its own. */
  suppressError?: boolean;
}

/** Splits an E.164 value into {country, national} by matching a known dial code — used
 *  for values libphonenumber-js can't fully parse yet (too short mid-typing, etc). */
function splitByKnownDialCode(v: string): { country: CountryCode; national: string } | null {
  // Longest dial code first so e.g. "+1" doesn't shadow "+1242" (Bahamas).
  const match = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length).find((c) => v.startsWith(c.dialCode));
  return match ? { country: match.code, national: v.slice(match.dialCode.length) } : null;
}

/** Parses an E.164 value into {country, national}, falling back to a dial-code prefix match
 *  when the value is too short/incomplete for libphonenumber-js to parse at all. */
function splitPhoneValue(v: string): { country: CountryCode; national: string } {
  if (!v) return { country: DEFAULT_COUNTRY, national: "" };
  const parsed = parsePhoneNumberFromString(v);
  if (parsed) return { country: (parsed.country as CountryCode) ?? DEFAULT_COUNTRY, national: parsed.nationalNumber };
  if (v.startsWith("+")) return splitByKnownDialCode(v) ?? { country: DEFAULT_COUNTRY, national: "" };
  return { country: DEFAULT_COUNTRY, national: v };
}

export function PhoneNumberField({ value, onChange, placeholder, disabled, className, suppressError }: PhoneNumberFieldProps) {
  const t = useTranslations("common");

  // The typed national number lives in local state, not re-derived from `value`
  // every render — libphonenumber-js returns `undefined` for a number that's
  // too short to parse yet (e.g. one digit typed), which would otherwise wipe
  // the field back to empty on every keystroke while `value` quietly kept the
  // real (unparseable-so-far) digits underneath.
  const [country, setCountry] = useState<CountryCode>(() => splitPhoneValue(value).country);
  const [nationalNumber, setNationalNumber] = useState<string>(() => splitPhoneValue(value).national);
  // Tracks the last E.164 string *this component* produced, so the sync effect
  // below can tell "the parent echoed back what we just sent" (ignore) apart
  // from "the parent changed `value` on its own" (re-sync from it) — e.g. a
  // different property's data loading in after this field already mounted.
  const lastEmittedRef = useRef<string>(value);

  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    const split = splitPhoneValue(value);
    setCountry(split.country);
    setNationalNumber(split.national);
  }, [value]);

  const selectedCountry = COUNTRIES.find((c) => c.code === country) ?? COUNTRIES.find((c) => c.code === DEFAULT_COUNTRY)!;

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const validation = useMemo(() => validatePhoneNumber(value, country), [value, country]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) searchInputRef.current?.focus();
  }, [isOpen]);

  function toggleOpen() {
    if (!isOpen) setSearch("");
    setIsOpen((prev) => !prev);
  }

  function emit(nextCountry: CountryCode, nextNational: string) {
    const next = nextNational ? `+${getCountryCallingCode(nextCountry)}${nextNational}` : "";
    lastEmittedRef.current = next;
    onChange(next);
  }

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dialCode.includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [search]);

  function updateNumber(rawNational: string) {
    const digits = rawNational.replace(/\D/g, "");
    // Cap the emitted "+<dialCode><digits>" string at the API's own limit — a
    // real number never gets close to this, but nothing stops someone pasting
    // a much longer string of digits in.
    const maxDigits = Math.max(0, PHONE_MAX_LENGTH - selectedCountry.dialCode.length);
    const capped = digits.slice(0, maxDigits);
    setNationalNumber(capped);
    emit(country, capped);
  }

  function selectCountry(code: CountryCode) {
    setCountry(code);
    setIsOpen(false);
    emit(code, nationalNumber);
  }

  const hasError = !suppressError && !validation.isValid;

  return (
    <div className={className}>
      <div
        ref={containerRef}
        className={cn(
          "relative flex h-11.5 w-full items-stretch rounded-xl border bg-white transition-all",
          hasError ? "border-red-500" : "border-secondary/20 focus-within:border-primary",
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={toggleOpen}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className="flex shrink-0 items-center gap-1.5 rounded-l-xl py-2 pl-4 pr-2 text-sm text-secondary transition-colors hover:bg-secondary/5 disabled:cursor-not-allowed"
        >
          {selectedCountry.dialCode}
          <Icon name="chevron-down" size={14} className={cn("text-muted transition-transform", isOpen && "rotate-180")} />
        </button>

        <div className="my-2 w-px shrink-0 bg-secondary/20" />

        <input
          type="tel"
          value={nationalNumber}
          onChange={(e) => updateNumber(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="min-w-0 flex-1 rounded-r-xl bg-transparent px-3 py-2 text-sm text-secondary placeholder:text-muted outline-none disabled:cursor-not-allowed"
        />

        {isOpen && (
          <div
            role="listbox"
            className="absolute inset-s-0 top-full z-50 mt-2 max-h-80 w-80 overflow-hidden rounded-xl border border-secondary/10 bg-card shadow-lg"
          >
            <div className="border-b border-secondary/10 p-2">
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("searchCountry")}
                className="w-full rounded-lg border border-secondary/20 px-3 py-2 text-sm text-secondary placeholder:text-muted outline-none focus:border-primary"
              />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {filteredCountries.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  role="option"
                  aria-selected={c.code === country}
                  onClick={() => selectCountry(c.code)}
                  className={cn(
                    "flex w-full items-center gap-3 px-4 py-2.5 text-start text-sm transition-colors hover:bg-secondary/5",
                    c.code === country && "bg-secondary/5",
                  )}
                >
                  <span className="w-12 shrink-0 font-medium text-secondary">{c.dialCode}</span>
                  <span className="truncate text-secondary/80">{c.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {hasError && <p className="mt-1 text-xs text-red-500">{validation.error}</p>}
    </div>
  );
}
